import { useEffect } from 'react';
import { useStore } from 'react-redux';
import { AppState } from 'store';
import { useAppDispatch } from 'store/hooks';
import { setLastUsedAvatar } from 'store/ScriptStudioSlice';
import { ScriptStudioBuffersState } from 'store/ScriptStudioBuffersSlice';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import useProjectSections from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import {
  Section,
  StudioProjectVersion,
} from '../../../../../server/graphql/__generated.types';
import { FALLBACK_AVATAR } from '../../constants';

const getCurrentTakeAvatars = (sections: Section[]) => {
  return sections
    .filter(section => section && section.currentTake !== null)
    .map(section => {
      return {
        avatarId: section.currentTake!.avatarId,
        avatarVariantId: section.currentTake!.avatarVariantId,
        avatarVariantVersion: section.currentTake!.ttsVersion,
        editedAt: new Date(section.currentTake!.createdAt).getTime(),
      };
    });
};

const getSectionDraftAvatars = (
  sections: Section[],
  scriptStudioBuffers: ScriptStudioBuffersState
) => {
  const { byId } = scriptStudioBuffers;

  return sections
    .filter(section => section != null)
    .map(section => {
      return {
        avatarId:
          byId[section.id]?.avatarDraft ??
          section.defaultAvatarId ??
          FALLBACK_AVATAR.avatarId,
        avatarVariantId:
          byId[section.id]?.avatarVariantDraft ??
          section.defaultAvatarVariantId ??
          FALLBACK_AVATAR.avatarVariantId,
        avatarVariantVersion: byId[section.id]?.avatarVariantVersionDraft ?? '',
        editedAt: byId[section.id]?.lastEditedAt ?? section.createdAt,
      };
    });
};

/*
 * Initialize the project level last used avatar to the most recently created
 * current take avatar or the most recently edited section draft avatar
 */
const useInitializeLastUsedAvatar = (projectId: string) => {
  const { sections } = useProjectSections({ projectId });
  const dispatch = useAppDispatch();
  const store = useStore<AppState>();
  const { studioVersion } = useStudioProjectContext();

  useEffect(() => {
    const currentTakeAvatars = getCurrentTakeAvatars(sections);
    const sectionDraftAvatars = getSectionDraftAvatars(
      sections,
      store.getState().scriptStudioBuffers
    );
    const sectionAvatars = currentTakeAvatars.concat(sectionDraftAvatars);
    const mostRecentAvatars = sectionAvatars.sort(
      (a, b) => b.editedAt - a.editedAt
    );

    if (mostRecentAvatars.length === 0) {
      // initializes lastUseAvatar as the default - Selene
      return;
    }

    dispatch(
      setLastUsedAvatar({
        id: mostRecentAvatars[0].avatarId,
        variantId: mostRecentAvatars[0].avatarVariantId,
        variantVersion:
          studioVersion === StudioProjectVersion.Universal
            ? mostRecentAvatars[0].avatarVariantVersion ?? ''
            : undefined,
      })
    );

    // intentionally does not depend on sections to only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, store]);
};

export default useInitializeLastUsedAvatar;
